import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SessionLegalEntity, SessionRole, SessionUser } from '@/entities/session'
import { Avatar } from '@/shared/ui/Avatar'
import { Button } from '@/shared/ui/Button'
import { Divider } from '@/shared/ui/Divider'
import { Icon } from '@/shared/ui/Icon'
import { Menu, type MenuGroupItem, type MenuItem } from '@/shared/ui/Menu'
import { Popover } from '@/shared/ui/Popover'
import { SectionCaption } from '@/shared/ui/SectionCaption'
import s from './AccountMenu.module.css'

export type AccountMenuProps = {
  /** Сотрудник сеанса. Имени в учётной системе нет — сотрудника называет логин. */
  user: SessionUser
  /**
   * Активное юрлицо. `null` — юрлица нет: вместо названия — «Компания не выбрана».
   * Не передан — компании у сеанса нет по смыслу (сотрудник импортёра работает за клиентов,
   * а не от своего юрлица): ни подписи на кнопке, ни блока в панели.
   */
  company?: SessionLegalEntity | null
  /** Роль сеанса — только подпись в профиле. Состав меню от неё не зависит. */
  role?: SessionRole
  /** «Сменить компанию». Не передан — юрлицо у логина одно, и пункта нет вовсе. */
  onSwitchCompany?: () => void
  onLogout: () => void
  /**
   * Группы настроек внутри меню: тема и язык на узком экране, где им нет места в шапке.
   * Стоят между действиями с компанией и выходом, каждая за своей чертой.
   */
  preferences?: MenuGroupItem[]
  /**
   * Идёт смена, запущенная из меню, — сейчас загрузка словаря выбранного языка на узком экране.
   * Кнопка показывает ожидание и меню не открывает: иначе выбор выглядел бы проигнорированным.
   */
  busy?: boolean
}

/**
 * Аккаунт в шапке кабинета: кто вошёл и от имени какого юрлица работает. Кнопка показывает
 * компанию при любой ширине, логин — с 768px. Панель — профиль (логин, почта), компания
 * с УНП, смена компании, настройки и выход.
 *
 * Данных и запросов у виджета нет: сеанс, выход и переход на выбор компании приносит раскладка
 * кабинета. Поэтому он и на стенде живёт без входа и мокового сеанса.
 */
export function AccountMenu({
  user,
  company,
  role,
  onSwitchCompany,
  onLogout,
  preferences = [],
  busy = false,
}: AccountMenuProps) {
  const { t } = useTranslation()
  const { t: ta } = useTranslation('auth')
  const profileId = useId()
  const companyId = useId()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  // Название юрлица в учётной системе может быть пустым — тогда его называет УНП
  const companyName =
    company === undefined
      ? null
      : company === null
        ? ta('account.noCompany')
        : (company.name ?? company.unp)

  const items: MenuItem[] = []
  if (onSwitchCompany !== undefined) {
    items.push({
      id: 'switch-company',
      label: ta('account.switchCompany'),
      icon: <Icon name="swap" size="sm" />,
      onSelect: onSwitchCompany,
    })
  }
  for (const group of preferences) {
    if (items.length > 0) items.push({ kind: 'separator', id: `${group.id}-separator` })
    items.push(group)
  }
  if (items.length > 0) items.push({ kind: 'separator', id: 'logout-separator' })
  items.push({
    id: 'logout',
    label: ta('account.logout'),
    icon: <Icon name="logout" size="sm" />,
    tone: 'danger',
    onSelect: onLogout,
  })

  return (
    <>
      <Button
        ref={anchorRef}
        className={s.trigger}
        variant="secondary"
        size="md"
        loading={busy}
        iconLeft={<Avatar name={user.login} size="sm" tone="accent" />}
        iconRight={<Icon name="chevronDown" size="sm" />}
        aria-label={
          companyName === null
            ? ta('account.triggerNoCompany', { name: user.login })
            : ta('account.trigger', { name: user.login, company: companyName })
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={s.triggerText}>
          {companyName === null ? (
            // Без компании логин — единственная строка кнопки при любой ширине
            <span className={s.triggerSolo}>{user.login}</span>
          ) : (
            <>
              <span className={s.triggerName}>{user.login}</span>
              <span className={s.triggerCompany}>{companyName}</span>
            </>
          )}
        </span>
      </Button>

      <Popover anchorRef={anchorRef} open={open} onOpenChange={setOpen} placement="bottom-end">
        <div className={s.panel}>
          {/* Профиль и компания — не пункты: произвольный блок не может быть потомком role="menu" */}
          <div id={profileId} className={s.profile}>
            <Avatar name={user.login} size="lg" tone="accent" />
            <div className={s.stack}>
              <span className={s.fullName}>{user.login}</span>
              {user.email !== null ? <span className={s.email}>{user.email}</span> : null}
              {role !== undefined ? (
                <span className={s.email}>{ta(`account.role.${role}`)}</span>
              ) : null}
            </div>
          </div>

          {companyName === null ? null : (
            <>
              <Divider tone="subtle" spacing="xs" inset="sm" decorative />

              <div id={companyId} className={s.company}>
                <SectionCaption>{ta('account.companyCaption')}</SectionCaption>
                <span className={s.companyName}>{companyName}</span>
                {company !== null && company !== undefined ? (
                  <span className={s.caption}>
                    {ta('account.companyDetails', { unp: company.unp })}
                  </span>
                ) : null}
              </div>
            </>
          )}

          <Divider tone="subtle" spacing="xs" inset="sm" decorative />

          {/* Фокус сразу встаёт на пункт: профиль и компанию скринридер прочтёт описанием меню */}
          <Menu
            label={t('layout.accountMenu')}
            aria-describedby={companyName === null ? profileId : `${profileId} ${companyId}`}
            items={items}
            autoFocus
            onSelect={() => setOpen(false)}
          />
        </div>
      </Popover>
    </>
  )
}
